import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import type { FieldDescriptor } from '../lib/types.js';

interface Props {
  field: FieldDescriptor;
  value: unknown;
  onChange: (next: unknown) => void;
  /** Issue key or project key used to scope user pickers. */
  scope?: { issueKey?: string; projectKey?: string };
}

/**
 * Renders one Jira field from its own metadata.
 *
 * The descriptor comes from Jira's createmeta/editmeta for this user, so a
 * field only appears here if Jira has already said this person may set it.
 */
export function DynamicField({ field, value, onChange, scope }: Props): JSX.Element {
  const { t } = useI18n();
  const id = `field-${field.fieldId}`;
  const label = (
    <label htmlFor={id} className={field.required ? 'field-required' : undefined}>
      {field.name}
    </label>
  );

  const options = field.allowedValues.map((option) => ({
    value: String(option.id ?? option.value ?? option.name ?? ''),
    label: String(option.value ?? option.name ?? option.id ?? ''),
    children: (option.children ?? []) as Array<{ id?: string; value?: string }>,
  }));

  switch (field.kind) {
    case 'textarea':
      return (
        <div className="field">
          {label}
          <textarea
            id={id}
            value={String(value ?? '')}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      );

    case 'number':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            type="number"
            step="any"
            value={value === undefined || value === null ? '' : String(value)}
            onChange={(event) =>
              onChange(event.target.value === '' ? null : Number(event.target.value))
            }
          />
        </div>
      );

    case 'date':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            type="date"
            value={String(value ?? '')}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      );

    case 'datetime':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            type="datetime-local"
            value={String(value ?? '')}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      );

    case 'url':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            type="url"
            className="mono"
            value={String(value ?? '')}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      );

    case 'labels':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            value={Array.isArray(value) ? (value as string[]).join(', ') : String(value ?? '')}
            placeholder="a, b, c"
            onChange={(event) =>
              onChange(
                event.target.value
                  .split(',')
                  .map((item) => item.trim())
                  .filter(Boolean),
              )
            }
          />
        </div>
      );

    case 'select':
    case 'radio':
    case 'priority':
    case 'issuetype':
    case 'version':
    case 'component':
      return (
        <div className="field">
          {label}
          <select
            id={id}
            value={String(value ?? '')}
            onChange={(event) => onChange(event.target.value || null)}
          >
            <option value="">{t('common.none')}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );

    case 'multiselect':
    case 'checkbox':
    case 'multiversion':
      return (
        <div className="field">
          {label}
          <select
            id={id}
            multiple
            size={Math.min(options.length || 3, 6)}
            value={(Array.isArray(value) ? value : []).map(String)}
            onChange={(event) =>
              onChange([...event.target.selectedOptions].map((option) => option.value))
            }
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );

    case 'cascading':
      return <Cascading id={id} label={label} options={options} value={value} onChange={onChange} />;

    case 'user':
    case 'multiuser':
      return (
        <UserPicker
          id={id}
          label={label}
          multiple={field.kind === 'multiuser'}
          value={value}
          onChange={onChange}
          scope={scope}
        />
      );

    case 'parent':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            className="mono"
            placeholder="ABC-123"
            value={String(value ?? '')}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
          />
        </div>
      );

    case 'timetracking':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            placeholder="2d 4h"
            value={
              typeof value === 'object' && value !== null
                ? String((value as { originalEstimate?: string }).originalEstimate ?? '')
                : String(value ?? '')
            }
            onChange={(event) => onChange({ originalEstimate: event.target.value })}
          />
        </div>
      );

    case 'unsupported':
      return (
        <div className="field">
          {label}
          <textarea
            id={id}
            className="mono"
            placeholder='{"value": "…"}'
            value={typeof value === 'string' ? value : JSON.stringify(value ?? '', null, 2)}
            onChange={(event) => {
              try {
                onChange(JSON.parse(event.target.value));
              } catch {
                onChange(event.target.value);
              }
            }}
          />
          <div className="small muted">
            {field.schema.custom ?? field.schema.system ?? field.schema.type}
          </div>
        </div>
      );

    default:
      return (
        <div className="field">
          {label}
          <input id={id} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
        </div>
      );
  }
}

function Cascading({
  id,
  label,
  options,
  value,
  onChange,
}: {
  id: string;
  label: JSX.Element;
  options: Array<{ value: string; label: string; children: Array<{ id?: string; value?: string }> }>;
  value: unknown;
  onChange: (next: unknown) => void;
}): JSX.Element {
  const current = (value ?? {}) as { parent?: string; child?: string };
  const parent = options.find((option) => option.value === current.parent);

  return (
    <div className="field">
      {label}
      <select
        id={id}
        value={current.parent ?? ''}
        onChange={(event) => onChange({ parent: event.target.value || undefined })}
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {parent && parent.children.length > 0 ? (
        <select
          style={{ marginTop: 8 }}
          value={current.child ?? ''}
          onChange={(event) => onChange({ ...current, child: event.target.value || undefined })}
        >
          <option value="">—</option>
          {parent.children.map((child) => (
            <option key={String(child.id ?? child.value)} value={String(child.value ?? child.id)}>
              {String(child.value ?? child.id)}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

function UserPicker({
  id,
  label,
  multiple,
  value,
  onChange,
  scope,
}: {
  id: string;
  label: JSX.Element;
  multiple: boolean;
  value: unknown;
  onChange: (next: unknown) => void;
  scope?: { issueKey?: string; projectKey?: string };
}): JSX.Element {
  const { t } = useI18n();
  const [query, setQuery] = useState('');

  const { data } = useQuery({
    queryKey: ['userpicker', scope?.issueKey ?? scope?.projectKey ?? '', query],
    queryFn: () => {
      if (scope?.issueKey) {
        return api<{ users: Array<{ accountId: string; displayName: string }> }>(
          `/issues/${scope.issueKey}/assignable${query ? `?q=${encodeURIComponent(query)}` : ''}`,
        );
      }
      return Promise.resolve({ users: [] });
    },
    enabled: Boolean(scope?.issueKey),
  });

  const selected = multiple
    ? (Array.isArray(value) ? value : []).map(String)
    : value
      ? [String(value)]
      : [];

  return (
    <div className="field">
      {label}
      <input
        id={id}
        value={query}
        placeholder={t('assign.search')}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="scroll-x" style={{ marginTop: 8 }}>
        {(data?.users ?? []).map((user) => {
          const active = selected.includes(user.accountId);
          return (
            <button
              key={user.accountId}
              type="button"
              className={`chip ${active ? 'chip-indeterminate' : ''}`}
              onClick={() => {
                if (multiple) {
                  onChange(
                    active
                      ? selected.filter((item) => item !== user.accountId)
                      : [...selected, user.accountId],
                  );
                } else {
                  onChange(active ? null : user.accountId);
                }
              }}
            >
              {user.displayName}
            </button>
          );
        })}
      </div>
    </div>
  );
}
